import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Container,
    CircularProgress
} from '@mui/material';
import {
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';

interface VerificationGuardProps {
    children: React.ReactNode;
}

export const VerificationGuard: React.FC<VerificationGuardProps> = ({ children }) => {
    const { user, switchRole } = useAuth();
    const location = useLocation();
    const [isSwitching, setIsSwitching] = useState(false);

    const handleSwitchToTrade = async () => {
        setIsSwitching(true);
        try {
            await switchRole('trade_company');
        } catch (err) {
            console.error('Failed to switch role from verification guard:', err);
            enqueueSnackbar('Failed to switch to Trade Business mode. Please try again.', { variant: 'error' });
        } finally {
            setIsSwitching(false);
        }
    };

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Only check for customers (or dual role active as customer)
    const isCustomer = user.account_type === 'customer' || (user.account_type === 'dual' && user.active_role === 'customer');

    if (isCustomer) {
        if (user.customer_status === 'PENDING' || user.customer_status === 'PENDING_VERIFICATION') {
            return (
                <Box sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    py: 12,
                    px: { xs: 4, sm: 6, lg: 8 }
                }}>
                    <Container maxWidth="sm">
                        <Box textAlign="center" mb={4}>
                            <Typography variant="h4" fontWeight="bold" color="text.primary">
                                Account Verification Pending
                            </Typography>
                        </Box>

                        <Card sx={{ textAlign: 'center', p: 4, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ mb: 2, color: 'warning.main', display: 'flex', justifyContent: 'center' }}>
                                    <WarningIcon sx={{ fontSize: 48 }} />
                                </Box>
                                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                                    Your client company account is currently pending verification. Our team is reviewing your application.
                                    You will be notified once your account is approved.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Typical review time: 24-48 hours.
                                </Typography>

                                {user.account_type === 'dual' && user.trade_company_id && (
                                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            You have a Trade Business profile linked to this account.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            disabled={isSwitching}
                                            startIcon={isSwitching ? <CircularProgress size={16} color="inherit" /> : undefined}
                                            onClick={handleSwitchToTrade}
                                        >
                                            {isSwitching ? 'Switching Mode...' : 'Switch to Trade Business Mode'}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Container>
                </Box>
            );
        }

        if (user.customer_status === 'REJECTED') {
            return (
                <Box sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    py: 12,
                    px: { xs: 4, sm: 6, lg: 8 }
                }}>
                    <Container maxWidth="sm">
                        <Box textAlign="center" mb={4}>
                            <Typography variant="h4" fontWeight="bold" color="text.primary">
                                Account Action Required
                            </Typography>
                        </Box>

                        <Card sx={{ textAlign: 'center', p: 4, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ mb: 2, color: 'error.main', display: 'flex', justifyContent: 'center' }}>
                                    <ErrorIcon sx={{ fontSize: 48 }} />
                                </Box>
                                <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                                    Unfortunately, we couldn't approve your client company account with the current details.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Please review your email for the rejection reason and update your profile to resubmit.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => window.location.href = '/settings/profile'}
                                    sx={{ mt: 2 }}
                                >
                                    Update Profile
                                </Button>

                                {user.account_type === 'dual' && user.trade_company_id && (
                                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            You have a Trade Business profile linked to this account.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            disabled={isSwitching}
                                            startIcon={isSwitching ? <CircularProgress size={16} color="inherit" /> : undefined}
                                            onClick={handleSwitchToTrade}
                                        >
                                            {isSwitching ? 'Switching Mode...' : 'Switch to Trade Business Mode'}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Container>
                </Box>
            );
        }
    }

    return <>{children}</>;
};
